import { validatePhoneTenDigits } from '../../../../shared/utils';
import { z } from 'zod';
import { ContractorProfile } from './Contractor/schema';
import { ContractorRole } from '../../../enums/users';

export const GuestCompanySchema = z.object({
    name: z.string().min(1, { message: 'Required' }),
    allowsMultipleDomains: z.boolean(),
    isActive: z.boolean(),
    primaryContact: z.string().min(1, { message: 'Required' }),
    phone: z.string().min(1, { message: 'Required' }),
    streetAddress: z.string().min(1, { message: 'Required' }),
    postcode: z.string().min(1, { message: 'Required' }),
    suburb: z.string().min(1, { message: 'Required' }),
    state: z.string().min(1, { message: 'Required' }),
    countryCode: z.enum(['AU'], { message: 'Required' }),
});

export const OwnerCompanySchema = z
    .object({
        notificationsEmail: z.string().min(1, { message: 'Required' }),
    })
    .merge(GuestCompanySchema);

export const BaseUserSchema = z.object({
    email: z.string().email(),
    firstName: z.string().min(1, { message: 'Required' }),
    lastName: z.string().min(1, { message: 'Required' }),
    phone: z.string().refine(validatePhoneTenDigits, {
        message: 'Enter exactly 10 digits (numbers only)',
    }),
    isActive: z.boolean(),
    profilePicture: z
        .object({
            id: z.string(),
            src: z.string(),
        })
        .optional(),
    vehiclePlate: z.string().default(''),
});

export const OwnerSchema = z
    .object({
        company: OwnerCompanySchema,
    })
    .merge(BaseUserSchema);

export const GuestSchema = z
    .object({
        department: z
            .object({
                id: z.number().nullable().optional(),
            })
            .nullable()
            .optional()
            .transform((department) => (!department?.id ? null : department)),
        company: GuestCompanySchema,
    })
    .merge(BaseUserSchema);

export const getProfileSchema = ({
    contractorCompany,
}: {
    contractorCompany: boolean;
}) => {
    return z.discriminatedUnion('role', [
        z
            .object({
                role: z.enum([
                    'guest_user',
                    'guest_admin',
                    'guest_department_admin',
                ]),
            })
            .merge(GuestSchema),
        z
            .object({
                role: z.enum(['owner_user', 'owner_admin']),
            })
            .merge(OwnerSchema),
        z.object({
            role: z.literal('guest_company'),
            company: GuestCompanySchema,
        }),
        z.object({
            role: z.literal('owner_company'),
            company: OwnerCompanySchema,
        }),
        z
            .object({
                role: z.literal('admin'),
            })
            .merge(BaseUserSchema),
        z
            .object({
                role: z.enum([ContractorRole.Admin, ContractorRole.User]),
            })
            .merge(contractorCompany ? ContractorProfile : BaseUserSchema),
        z
            .object({
                role: z.literal('external_attendee_user'),
            })
            .merge(BaseUserSchema),
    ]);
};
